import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { arrayHasItems, removeLoadingKey } from '../helpers/common'
import { toast } from 'react-toastify'

export const ProductCostsLoadingKeys = {
  costs: "ALL_PRODUCT_COST",
  initProductFromShiphero: "INIT_PRODUCT_FROM_SHIPHERO",
  deleteProduct: "DELETE_PRODUCT",
  syncLandedCost: "SYNC_LANDED_COST",
  updatePhasedStatus: "UPDATE_PHASED_STATUS",
  updateSingleProductCost: "UPDATE_SINGLE_PRODUCT_COST",
  getTotalInventory: "GET_TOTAL_INVENTORY"
}

const initialState = {
    loading: true,
    loadingKeys: [],
    isFirstLoadComplete: false,
    error: null,
    productCosts: [],
    allProductCosts: [],
    pagination: {
      isTheLastPage: false,
      rangeRows: "",
      totalPages: 0
    },
    totalInventoryValue: null
}

export const updateSingleProductInProductCosts = createAsyncThunk(
  'productCost/updateSingleProductInProductCosts',
  async ({
    productId,
    search,
    amazon,
    isReplenishable,
    kit,
    phasedStatuses
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/product-costs-data-by-id`, { params: {
        productId,
        search,
        amazon,
        isReplenishable,
        kit,
        phasedStatuses
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const syncProductToShopify = createAsyncThunk(
  'productCost/syncProductToShopify',
  async ({ productsToShopifySync }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/product-costs/sync-product-to-shopify`, { productsToShopifySync });
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ ...data });
        }, 2000);
      });
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const initProductFromShipheroRequest = createAsyncThunk(
  'productCost/initProductFromShipheroRequest',
  async ({
    productSku
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/init-product-from-shiphero`, { productSku });
      return data;
    } catch (error) {
      const errorMessage = arrayHasItems(error?.response?.data?.error?.response?.errors) ? error?.response?.data?.error?.response?.errors?.map((error) => error?.message || "").join(",") : error?.message || 'Request failed';
      return rejectWithValue({ error: errorMessage || 'Request failed' });
    }
  }
);

export const updateProductFromShipheroRequest = createAsyncThunk(
  'productCost/updateProductFromShipheroRequest',
  async ({ sku, state, productData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/product-costs/update-product-from-shiphero`, { sku, state, productData });
      return data;
    } catch (error) {
      const errorMessage = Array.isArray(error?.response?.data?.response?.errors)
        ? error.response.data.response.errors.map((e) => e?.message || '').join(',')
        : (error?.response?.data?.error || error?.message || 'Request failed');
      return rejectWithValue({ error: errorMessage });
    }
  }
)

export const updatePhasedStatus = createAsyncThunk(
    'productCost/updatePhasedStatus',
    async ({
      productId, 
      newPhasedStatus,
      phasedStatuses,
      search, 
      page, 
      itemsPerPage,
      sortBy,
      sortDirection 
    }, { rejectWithValue, dispatch }) => {
      try {
        const { data } = await axios.post(`/api/v1/update-phased-status`, { productId, newPhasedStatus });
        await dispatch(getAllPC({
          search, 
          page, 
          phasedStatuses,
          itemsPerPage,
          sortBy,
          sortDirection 
        }));
        return data;
      } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
      }
    }
);

export const deleteProduct = createAsyncThunk(
  'productCost/deleteProduct',
  async ({
    productId,
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/delete-product`, { productId });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const getAllPC = createAsyncThunk(
    'productCost/getAllPC',
    async (params, { rejectWithValue }) => {
      try {
        const { data } = await axios.get(`/api/v1/product-costs`, { params });
        return data;
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.message === "canceled") {
          throw new Error("AbortError");
        }
        if ('string' === typeof error.response.data) {
          return rejectWithValue(error.response.data)
        } else {
          return rejectWithValue(error.response.data.errors || error.response.data.error)
        }
      }
    }
)

export const getTotalInventoryValues = createAsyncThunk(
  'productCost/getTotalInventoryValues',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/get-total-inventory-values');
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const productCostSlice = createSlice({
    name: 'productCost',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(getAllPC.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.costs];
        })
        .addCase(getAllPC.fulfilled, (state, action) => {
            state.loading = false;
            state.productCosts = action.payload?.items;
            state.allProductCosts = action.payload?.allItems;
            state.pagination.isTheLastPage = action.payload?.isTheLastPage;
            state.pagination.rangeRows = action.payload?.rangeRows;
            state.pagination.totalPages = action.payload?.totalPages;
            state.isFirstLoadComplete = true;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.costs, state.loadingKeys)
        })
        .addCase(getAllPC.rejected, (state, action) => {
            if (action.error?.message === "AbortError") {
              return;
            }
            state.loading = false;
            state.productCosts = [];
            state.pagination.isTheLastPage = false;
            state.pagination.rangeRows = "";
            state.pagination.totalPages = 0;
            state.isFirstLoadComplete = true;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.costs, state.loadingKeys)
        })
        .addCase(initProductFromShipheroRequest.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.initProductFromShiphero];
        })
        .addCase(initProductFromShipheroRequest.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.initProductFromShiphero, state.loadingKeys);
        })
        .addCase(initProductFromShipheroRequest.rejected, (state, action) => {
            state.loading = false;
            const errorMessage = action?.payload?.error || action.error?.message || 'Request failed';
            state.error = errorMessage;
            toast.error(errorMessage || 'Request failed', { position: "bottom-right" });
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.initProductFromShiphero, state.loadingKeys);
        })
        .addCase(updateProductFromShipheroRequest.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.initProductFromShiphero];
        })
        .addCase(updateProductFromShipheroRequest.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.initProductFromShiphero, state.loadingKeys);
          toast.success(`Product updated successfully.`, { position: "bottom-right" });
        })
        .addCase(updateProductFromShipheroRequest.rejected, (state, action) => {
          state.loading = false;
          const errorMessage = action?.payload?.error || action.error?.message || 'Request failed';
          state.error = errorMessage;
          toast.error(errorMessage || 'Request failed', { position: "bottom-right" });
          state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.initProductFromShiphero, state.loadingKeys);
        })
        .addCase(updatePhasedStatus.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.updatePhasedStatus];
        })
        .addCase(updatePhasedStatus.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.updatePhasedStatus, state.loadingKeys);
        })
        .addCase(updatePhasedStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.updatePhasedStatus, state.loadingKeys);
        })
        .addCase(deleteProduct.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.deleteProduct];
        })
        .addCase(deleteProduct.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.deleteProduct, state.loadingKeys);
            toast.success(
              `The product is deleted successfully.`, { position: "bottom-right" }
            )
        })
        .addCase(deleteProduct.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.deleteProduct, state.loadingKeys);
            toast.error(
              `The product can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(syncProductToShopify.pending, (state, action) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.syncLandedCost];
        })
        .addCase(syncProductToShopify.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.syncLandedCost, state.loadingKeys);
            toast.success(
              `Product synced successfully.`, 
              { position: "bottom-right" }
            )
        })
        .addCase(syncProductToShopify.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.syncLandedCost, state.loadingKeys);
            const errorMessage = action?.payload?.error || action.error?.message;
            toast.error(
              `${errorMessage}`, { position: "bottom-right" }
            )
        })
        .addCase(updateSingleProductInProductCosts.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.updateSingleProductCost];
        })
        .addCase(updateSingleProductInProductCosts.fulfilled, (state, action) => {
            const product = action?.payload;
            const index = state.productCosts.findIndex(item => item.id === product.id);
            if (index !== -1) {
              state.productCosts[index] = product;
            }
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.updateSingleProductCost, state.loadingKeys)
        })
        .addCase(updateSingleProductInProductCosts.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.updateSingleProductCost, state.loadingKeys)
        })
        .addCase(getTotalInventoryValues.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductCostsLoadingKeys.getTotalInventory];
        })
        .addCase(getTotalInventoryValues.fulfilled, (state, action) => {
          state.loading = false;
          state.totalInventoryValue = action.payload;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.getTotalInventory, state.loadingKeys);
        })
        .addCase(getTotalInventoryValues.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductCostsLoadingKeys.getTotalInventory, state.loadingKeys);
          // toast.error(
          //   `Failed to get inventory value. Error - ${action.error?.message}.`, 
          //   { position: "bottom-right" }
          // );
        })
    }
})

export const {} = productCostSlice.actions;

export default productCostSlice.reducer